import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiActivity, FiUser, FiClock, FiDollarSign, FiArrowRight,
  FiRefreshCw, FiZap, FiAlertCircle, FiCheckCircle, FiXCircle,
  FiTrendingUp, FiCreditCard, FiPlay, FiPause, FiFilter, FiSearch
} from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';
import SafeIcon from '@/components/SafeIcon';

interface BillingEvent {
  id: string;
  user_id: string | null;
  user_email: string | null;
  event_type: string;
  event_source: string;
  plan_id: string | null;
  previous_state: string | null;
  new_state: string | null;
  payment_method: string | null;
  amount_usd: number | null;
  currency: string | null;
  invoice_id: string | null;
  stripe_event_id: string | null;
  tx_hash: string | null;
  metadata: Record<string, unknown>;
  actor_id: string | null;
  actor_type: string | null;
  is_anomaly: boolean | null;
  anomaly_type: string | null;
  anomaly_severity: string | null;
  created_at: string;
}

const EVENT_CONFIG: Record<string, { 
  icon: React.ComponentType; 
  color: string; 
  label: string;
  bgColor: string;
}> = {
  trial_started: { icon: FiPlay, color: 'text-cyan-400', label: 'Trial Started', bgColor: 'bg-cyan-500/10' },
  trial_converted: { icon: FiTrendingUp, color: 'text-green-400', label: 'Trial Converted', bgColor: 'bg-green-500/10' },
  trial_expired: { icon: FiXCircle, color: 'text-orange-400', label: 'Trial Expired', bgColor: 'bg-orange-500/10' },
  trial_will_end: { icon: FiAlertCircle, color: 'text-yellow-400', label: 'Trial Ending Soon', bgColor: 'bg-yellow-500/10' },
  subscription_renewed: { icon: FiRefreshCw, color: 'text-green-400', label: 'Subscription Renewed', bgColor: 'bg-green-500/10' },
  subscription_expired: { icon: FiAlertCircle, color: 'text-red-400', label: 'Subscription Expired', bgColor: 'bg-red-500/10' },
  subscription_updated: { icon: FiRefreshCw, color: 'text-blue-400', label: 'Subscription Updated', bgColor: 'bg-blue-500/10' },
  subscription_canceled: { icon: FiXCircle, color: 'text-red-400', label: 'Subscription Canceled', bgColor: 'bg-red-500/10' },
  invoice_paid: { icon: FiCheckCircle, color: 'text-green-400', label: 'Invoice Paid', bgColor: 'bg-green-500/10' },
  invoice_expired: { icon: FiXCircle, color: 'text-red-400', label: 'Invoice Expired', bgColor: 'bg-red-500/10' },
  plan_upgraded: { icon: FiTrendingUp, color: 'text-purple-400', label: 'Plan Upgraded', bgColor: 'bg-purple-500/10' },
  plan_downgraded: { icon: FiTrendingUp, color: 'text-yellow-400', label: 'Plan Downgraded', bgColor: 'bg-yellow-500/10' },
  payment_succeeded: { icon: FiDollarSign, color: 'text-green-400', label: 'Payment Succeeded', bgColor: 'bg-green-500/10' },
  payment_failed: { icon: FiXCircle, color: 'text-red-400', label: 'Payment Failed', bgColor: 'bg-red-500/10' },
  crypto_payment_confirmed: { icon: FiCheckCircle, color: 'text-green-400', label: 'Crypto Payment Confirmed', bgColor: 'bg-green-500/10' },
  crypto_invoice_created: { icon: FiCreditCard, color: 'text-cyan-400', label: 'Crypto Invoice Created', bgColor: 'bg-cyan-500/10' },
  crypto_invoice_expired: { icon: FiXCircle, color: 'text-orange-400', label: 'Crypto Invoice Expired', bgColor: 'bg-orange-500/10' },
  checkout_started: { icon: FiCreditCard, color: 'text-blue-400', label: 'Checkout Started', bgColor: 'bg-blue-500/10' },
  user_signup: { icon: FiUser, color: 'text-blue-400', label: 'New Signup', bgColor: 'bg-blue-500/10' },
};

const getEventConfig = (eventType: string) => {
  return EVENT_CONFIG[eventType] || { 
    icon: FiActivity, 
    color: 'text-gray-400', 
    label: eventType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    bgColor: 'bg-gray-500/10'
  };
};

const formatTimeAgo = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return `${diffSecs}s ago`;
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

const getPlanLabel = (planId: string | null) => {
  if (!planId) return null;
  const labels: Record<string, string> = {
    starter: 'Starter',
    pro_trader: 'Pro Trader',
    institutional_ai: 'Institutional AI',
    enterprise: 'Enterprise'
  };
  return labels[planId] || planId;
};

const LiveEventsStream: React.FC = () => {
  const [events, setEvents] = useState<BillingEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [newEventCount, setNewEventCount] = useState(0);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterProvider, setFilterProvider] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchEvents = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('billing_events')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setEvents((data as BillingEvent[]) || []);
    } catch (err) {
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();

    // Set up real-time subscription
    const channel = supabase
      .channel('billing-events-stream')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'billing_events'
        },
        (payload) => {
          if (!isPaused) {
            setEvents(prev => [payload.new as BillingEvent, ...prev.slice(0, 49)]);
          } else {
            setNewEventCount(prev => prev + 1);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchEvents, isPaused]);

  const handleResume = () => {
    setIsPaused(false);
    setNewEventCount(0);
    fetchEvents();
  };

  // Filter events
  const filteredEvents = useMemo(() => {
    return events.filter(event => {
      const matchesType = filterType === 'all' || event.event_type.includes(filterType);
      const matchesProvider = filterProvider === 'all' || 
        (filterProvider === 'stripe' && event.event_source?.includes('stripe')) ||
        (filterProvider === 'crypto' && (event.event_source?.includes('crypto') || event.payment_method?.includes('crypto'))) ||
        (filterProvider === 'admin' && event.actor_type === 'admin');
      const matchesSearch = !searchQuery || 
        event.user_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        event.event_type.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesType && matchesProvider && matchesSearch;
    });
  }, [events, filterType, filterProvider, searchQuery]);

  const seedDemoEvent = async () => {
    const eventTypes = ['trial_started', 'payment_succeeded', 'crypto_payment_confirmed', 'subscription_renewed', 'plan_upgraded'];
    const plans = ['starter', 'pro_trader', 'institutional_ai'];
    const methods = ['card', 'crypto_usdt', 'crypto_btc'];
    const sources = ['stripe_webhook', 'admin', 'cron', 'user'];
    
    const randomEvent = eventTypes[Math.floor(Math.random() * eventTypes.length)];
    const randomPlan = plans[Math.floor(Math.random() * plans.length)];
    const randomMethod = methods[Math.floor(Math.random() * methods.length)];
    const randomSource = sources[Math.floor(Math.random() * sources.length)];
    
    await supabase.rpc('log_billing_event', {
      p_event_type: randomEvent,
      p_event_source: randomSource,
      p_user_email: `demo${Math.floor(Math.random() * 1000)}@example.com`,
      p_plan_id: randomPlan,
      p_previous_state: 'trialing',
      p_new_state: 'active',
      p_payment_method: randomMethod,
      p_amount_usd: Math.floor(Math.random() * 150) + 29,
      p_actor_type: randomSource === 'admin' ? 'admin' : 'system'
    });
  };

  return (
    <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl overflow-hidden" data-testid="live-events-stream">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-700/50">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-600 to-cyan-600 flex items-center justify-center">
            <SafeIcon icon={FiActivity} className="text-white" size={20} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Live Events Stream</h3>
            <p className="text-sm text-gray-400">Real-time subscription activity</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Live indicator */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-700/50 rounded-full">
            <span className={`w-2 h-2 rounded-full ${isPaused ? 'bg-yellow-500' : 'bg-green-500 animate-pulse'}`} />
            <span className="text-xs text-gray-300">{isPaused ? 'Paused' : 'Live'}</span>
          </div>
          
          {/* Pause/Resume button */}
          <button
            onClick={() => isPaused ? handleResume() : setIsPaused(true)}
            className="p-2 rounded-lg bg-gray-700/50 hover:bg-gray-600/50 transition-colors"
          >
            <SafeIcon icon={isPaused ? FiPlay : FiPause} className="text-gray-300" size={16} />
          </button>
          
          {/* Refresh button */}
          <button
            onClick={fetchEvents}
            className="p-2 rounded-lg bg-gray-700/50 hover:bg-gray-600/50 transition-colors"
          >
            <SafeIcon icon={FiRefreshCw} className={`text-gray-300 ${loading ? 'animate-spin' : ''}`} size={16} />
          </button>
          
          {/* Demo event button */}
          <button
            onClick={seedDemoEvent}
            className="px-3 py-1.5 rounded-lg bg-purple-600/20 hover:bg-purple-600/30 text-purple-400 text-xs font-medium transition-colors"
          >
            <SafeIcon icon={FiZap} className="inline mr-1" size={12} />
            Seed Demo
          </button>
        </div>
        </div>

        {/* Filters Row */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
            <input
              type="text"
              placeholder="Search events..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
            />
          </div>
          
          {/* Event Type Filter */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-1.5 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-300 focus:outline-none focus:border-purple-500"
          >
            <option value="all">All Events</option>
            <option value="trial">Trial Events</option>
            <option value="payment">Payments</option>
            <option value="subscription">Subscriptions</option>
            <option value="crypto">Crypto</option>
          </select>
          
          {/* Provider Filter */}
          <select
            value={filterProvider}
            onChange={(e) => setFilterProvider(e.target.value)}
            className="px-3 py-1.5 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-slate-300 focus:outline-none focus:border-purple-500"
          >
            <option value="all">All Sources</option>
            <option value="stripe">Stripe</option>
            <option value="crypto">Crypto</option>
            <option value="admin">Admin</option>
          </select>
        </div>
      </div>

      {/* New events banner */}
      <AnimatePresence>
        {isPaused && newEventCount > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-cyan-500/10 border-b border-cyan-500/20 px-6 py-2"
          >
            <button
              onClick={handleResume}
              className="w-full flex items-center justify-center gap-2 text-cyan-400 text-sm hover:text-cyan-300 transition-colors"
            >
              <SafeIcon icon={FiZap} size={14} />
              {newEventCount} new event{newEventCount > 1 ? 's' : ''} - Click to load
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Events list */}
      <div className="max-h-[500px] overflow-y-auto">
        {loading ? (
          <div className="p-8 text-center">
            <SafeIcon icon={FiRefreshCw} className="mx-auto text-slate-500 animate-spin" size={24} />
            <p className="mt-2 text-sm text-slate-400">Loading events...</p>
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="p-8 text-center">
            <SafeIcon icon={FiActivity} className="mx-auto text-slate-500" size={32} />
            <p className="mt-2 text-slate-400">{events.length > 0 ? 'No matching events' : 'No events yet'}</p>
            <p className="text-sm text-slate-500 mt-1">
              {events.length > 0 ? 'Try adjusting your filters' : 'Events will appear here in real-time'}
            </p>
            {events.length === 0 && (
              <button
                onClick={seedDemoEvent}
                className="mt-4 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-cyan-600 text-white text-sm font-medium hover:opacity-90 transition-opacity"
              >
                Generate Demo Event
              </button>
            )}
          </div>
        ) : (
          <AnimatePresence initial={false}>
            {filteredEvents.map((event, index) => {
              const config = getEventConfig(event.event_type);
              const Icon = config.icon;
              
              return (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.2, delay: index * 0.02 }}
                  className="px-6 py-4 border-b border-slate-700/30 hover:bg-slate-700/20 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    {/* Event icon */}
                    <div className={`w-10 h-10 rounded-lg ${config.bgColor} flex items-center justify-center flex-shrink-0`}>
                      <SafeIcon icon={Icon} className={config.color} size={18} />
                    </div>
                    
                    {/* Event details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-sm font-medium ${config.color}`}>
                          {config.label}
                        </span>
                        {event.plan_id && (
                          <span className="px-2 py-0.5 rounded text-xs bg-gray-700/50 text-gray-300">
                            {getPlanLabel(event.plan_id)}
                          </span>
                        )}
                        {event.payment_method && (
                          <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-gray-700/50 text-gray-400">
                            <SafeIcon icon={FiCreditCard} size={10} />
                            {event.payment_method}
                          </span>
                        )}
                      </div>
                      
                      {event.user_email && (
                        <p className="text-sm text-gray-300 mt-1 truncate">
                          {event.user_email}
                        </p>
                      )}
                      
                      {event.is_anomaly && (
                        <div className="flex items-center gap-1 mt-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            event.anomaly_severity === 'high' ? 'bg-red-500/20 text-red-400' :
                            event.anomaly_severity === 'critical' ? 'bg-red-600/30 text-red-300' :
                            'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            <SafeIcon icon={FiAlertCircle} className="inline mr-1" size={10} />
                            {event.anomaly_type?.replace(/_/g, ' ')}
                          </span>
                        </div>
                      )}
                      
                      {(event.previous_state || event.new_state) && (
                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                          {event.previous_state && (
                            <span className="px-1.5 py-0.5 rounded bg-gray-700/30">
                              {event.previous_state}
                            </span>
                          )}
                          {event.previous_state && event.new_state && (
                            <SafeIcon icon={FiArrowRight} size={10} />
                          )}
                          {event.new_state && (
                            <span className="px-1.5 py-0.5 rounded bg-gray-700/30">
                              {event.new_state}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Amount and time */}
                    <div className="text-right flex-shrink-0">
                      {event.amount_usd && (
                        <p className="text-sm font-medium text-green-400">
                          ${event.amount_usd}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 flex items-center gap-1 justify-end mt-1">
                        <SafeIcon icon={FiClock} size={10} />
                        {formatTimeAgo(event.created_at)}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        )}
      </div>
      
      {/* Footer stats */}
      <div className="px-6 py-3 bg-slate-900/30 border-t border-slate-700/50 flex items-center justify-between text-xs text-slate-500">
        <span>Showing {filteredEvents.length} of {events.length} events</span>
        <span className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          Auto-refresh enabled
        </span>
      </div>
    </div>
  );
};

export default LiveEventsStream;